import { useEffect, useMemo, useState, type ReactElement } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { Badge, Button, IconButton, Stack, Text, TextInput } from "@ux/primitives";
import { palette, radius, space } from "@ux/tokens";
import { useAssetAssignments } from "../asset-assignments/hooks";
import { useWorkspaces } from "../workspaces/hooks";
import { describeApiError } from "../common/errors";
import { formatAssetDisplay } from "./display";
import { useAssets } from "./hooks";
import type { AssetAssignmentView } from "../asset-assignments/types";
import type { WorkspaceView } from "../workspaces/types";
import type { AssetView } from "./types";

export interface AssetPickerDialogProps {
    readonly open: boolean;
    readonly targetWorkspace: WorkspaceView;
    /**
     * Fired when the picked asset is currently unassigned. The handler is
     * expected to create a new assignment against the target workspace.
     */
    readonly onAssignUnassigned: (asset: AssetView) => void;
    /**
     * Fired when the picked asset is currently assigned elsewhere AND the
     * user has confirmed the re-assignment. The handler is expected to
     * release the current assignment, then create a new one against the
     * target workspace.
     */
    readonly onReassignAsset: (asset: AssetView, currentAssignment: AssetAssignmentView) => void;
    readonly onCancel: () => void;
    readonly isWorking: boolean;
}

/**
 * Picker for the "Assign existing asset" path on the workspace-anchored
 * campaign workflow. Lists every active asset with a badge indicating
 * its current home (`Unassigned`, or the workspace name when it lives
 * somewhere else). Picking an unassigned asset fires the assignment
 * immediately; picking a placed asset surfaces an inline confirmation
 * strip in place of that row — *"Currently at Desk 8. Re-assign to
 * Desk 12?"* — so the re-assignment decision happens in the same field
 * of view as the choice that prompted it (no modal-on-modal, no losing
 * scroll position).
 *
 * Sorting: assets in the same `sageOfficeId` as the target workspace
 * float to the top, then everything else, alphabetized by their
 * friendly label. This matters in multi-office tenants where "Desk 12"
 * exists at NCUS HQ and SCUS Annex; the local-office-first ordering
 * reduces accidental cross-office re-assignments.
 */
export function AssetPickerDialog({
    open,
    targetWorkspace,
    onAssignUnassigned,
    onReassignAsset,
    onCancel,
    isWorking,
}: AssetPickerDialogProps): ReactElement | null {
    const [search, setSearch] = useState("");
    const [confirmingMoveOf, setConfirmingMoveOf] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            setSearch("");
            setConfirmingMoveOf(null);
        }
    }, [open]);

    const assetsQuery = useAssets({
        q: search.trim(),
        sageOfficeId: "",
        type: "",
        includeDeleted: false,
    });

    // Open assignments across the whole tenant — small dataset (one row per
    // currently-placed asset). Powers both the "Currently at …" badges and
    // the move flow's release-then-create payload.
    const openAssignmentsQuery = useAssetAssignments({
        assetId: "",
        userId: "",
        workspaceId: "",
        targetKind: "",
        openOnly: true,
    });

    const workspacesQuery = useWorkspaces({
        q: "",
        sageOfficeId: "",
        type: "",
        includeDeleted: true,
    });

    const assignmentByAssetId = useMemo(() => {
        const map = new Map<string, AssetAssignmentView>();
        for (const a of openAssignmentsQuery.data ?? []) {
            map.set(a.assetId, a);
        }
        return map;
    }, [openAssignmentsQuery.data]);

    const workspaceIndex = useMemo(() => {
        const map = new Map<string, WorkspaceView>();
        for (const w of workspacesQuery.data ?? []) map.set(w.id, w);
        return map;
    }, [workspacesQuery.data]);

    const sortedAssets = useMemo(() => {
        const all = (assetsQuery.data ?? []).filter((a) => a.id !== "");
        // Same-office-first: an asset's office is the office of its
        // current assignment's workspace. Unassigned assets have no
        // office and sort with the "other" group, but keep their
        // alphabetical order within the bucket so the list stays scannable.
        const sameOffice: AssetView[] = [];
        const otherOffice: AssetView[] = [];
        for (const asset of all) {
            const assignment = assignmentByAssetId.get(asset.id);
            const ws = assignment?.assignedToWorkspaceId
                ? workspaceIndex.get(assignment.assignedToWorkspaceId)
                : null;
            if (ws !== null && ws !== undefined && ws.sageOfficeId === targetWorkspace.sageOfficeId) {
                sameOffice.push(asset);
            } else {
                otherOffice.push(asset);
            }
        }
        const cmp = (a: AssetView, b: AssetView): number =>
            formatAssetDisplay(a, a.id).localeCompare(formatAssetDisplay(b, b.id));
        sameOffice.sort(cmp);
        otherOffice.sort(cmp);
        return [...sameOffice, ...otherOffice];
    }, [assetsQuery.data, assignmentByAssetId, workspaceIndex, targetWorkspace.sageOfficeId]);

    if (!open) return null;

    const isSelfWorkspace = (assignment: AssetAssignmentView | undefined): boolean =>
        assignment?.assignedToWorkspaceId === targetWorkspace.id;

    function handleRowClick(asset: AssetView): void {
        const assignment = assignmentByAssetId.get(asset.id);
        if (assignment === undefined) {
            // Unassigned → no friction, just assign.
            onAssignUnassigned(asset);
            return;
        }
        if (isSelfWorkspace(assignment)) {
            // Already at this workspace; nothing to do.
            return;
        }
        setConfirmingMoveOf(asset.id);
    }

    function handleConfirmReassign(asset: AssetView): void {
        const assignment = assignmentByAssetId.get(asset.id);
        if (assignment === undefined) return;
        onReassignAsset(asset, assignment);
    }

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Assign existing asset"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: space[4],
                zIndex: 50,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel();
            }}
        >
            <div
                style={{
                    background: palette.canvas,
                    border: `1px solid ${palette.border}`,
                    borderRadius: radius.lg,
                    padding: space[5],
                    width: "min(640px, 100%)",
                    maxHeight: "min(80vh, 720px)",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                }}
            >
                <Stack direction="row" align="center" justify="space-between" gap={2}>
                    <Stack gap={1}>
                        <Text role="heading">Assign asset to {targetWorkspace.name}</Text>
                        <Text role="muted">{targetWorkspace.type}</Text>
                    </Stack>
                    <IconButton
                        icon={<X size={16} />}
                        label="Close"
                        tone="neutral"
                        onClick={onCancel}
                    />
                </Stack>

                <div style={{ marginTop: space[4] }}>
                    <Stack direction="row" align="center" gap={2}>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ flex: 1 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Tag, serial, make…"
                            />
                        </div>
                    </Stack>
                </div>

                <div
                    style={{
                        marginTop: space[4],
                        flex: 1,
                        overflow: "auto",
                        minHeight: 0,
                    }}
                >
                    {assetsQuery.isPending ? (
                        <Text role="muted">Searching assets…</Text>
                    ) : assetsQuery.isError ? (
                        <Text role="muted" color={palette.danger}>
                            Could not load assets — {describeApiError(assetsQuery.error)}
                        </Text>
                    ) : sortedAssets.length === 0 ? (
                        <Text role="muted">
                            No active assets match. Try a different search, or use “Add new asset”.
                        </Text>
                    ) : (
                        <Stack gap={2}>
                            {sortedAssets.map((asset) => {
                                const assignment = assignmentByAssetId.get(asset.id);
                                const ws = assignment?.assignedToWorkspaceId
                                    ? workspaceIndex.get(assignment.assignedToWorkspaceId)
                                    : null;
                                const here = isSelfWorkspace(assignment);
                                const confirming = confirmingMoveOf === asset.id;
                                return (
                                    <AssetPickerRow
                                        key={asset.id}
                                        label={formatAssetDisplay(asset, asset.id)}
                                        currentLocationLabel={
                                            here
                                                ? "Already here"
                                                : ws !== null && ws !== undefined
                                                  ? `Currently at ${ws.name} · ${ws.type}`
                                                  : assignment !== undefined
                                                    ? "Currently assigned"
                                                    : "Unassigned"
                                        }
                                        here={here}
                                        confirming={confirming}
                                        targetLabel={`${targetWorkspace.name} · ${targetWorkspace.type}`}
                                        isWorking={isWorking}
                                        onPick={() => handleRowClick(asset)}
                                        onConfirmReassign={() => handleConfirmReassign(asset)}
                                        onCancelReassign={() => setConfirmingMoveOf(null)}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </div>

                <div style={{ marginTop: space[4] }}>
                    <Stack direction="row" justify="flex-end">
                        <Button variant="ghost" onClick={onCancel} disabled={isWorking}>
                            Close
                        </Button>
                    </Stack>
                </div>
            </div>
        </div>
    );
}

interface AssetPickerRowProps {
    readonly label: string;
    readonly currentLocationLabel: string;
    readonly here: boolean;
    readonly confirming: boolean;
    readonly targetLabel: string;
    readonly isWorking: boolean;
    readonly onPick: () => void;
    readonly onConfirmReassign: () => void;
    readonly onCancelReassign: () => void;
}

function AssetPickerRow({
    label,
    currentLocationLabel,
    here,
    confirming,
    targetLabel,
    isWorking,
    onPick,
    onConfirmReassign,
    onCancelReassign,
}: AssetPickerRowProps): ReactElement {
    const baseStyle = {
        padding: `${space[3]} ${space[3]}`,
        background: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: 6,
    } as const;

    if (confirming) {
        // Confirmation strip replaces the row in place — keeps the user's
        // eyes on the same field of view they were already looking at.
        return (
            <div style={{ ...baseStyle, background: palette.surfaceInset }}>
                <Stack gap={2}>
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">{label}</Text>
                        <Stack direction="row" align="center" gap={1}>
                            <Text role="muted">{currentLocationLabel}</Text>
                            <ArrowRight size={14} style={{ opacity: 0.55 }} />
                            <Text role="muted">{targetLabel}</Text>
                        </Stack>
                    </Stack>
                    <Stack direction="row" gap={2} justify="flex-end">
                        <Button variant="ghost" size="sm" onClick={onCancelReassign} disabled={isWorking}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={onConfirmReassign}
                            disabled={isWorking}
                        >
                            {isWorking ? "Re-assigning…" : "Re-assign here"}
                        </Button>
                    </Stack>
                </Stack>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onPick}
            disabled={here || isWorking}
            style={{
                ...baseStyle,
                textAlign: "left",
                cursor: here ? "default" : "pointer",
                opacity: here ? 0.65 : 1,
                color: "inherit",
                font: "inherit",
                width: "100%",
            }}
        >
            <Stack direction="row" align="center" justify="space-between" gap={2}>
                <Stack gap={1}>
                    <Text role="body">{label}</Text>
                    <Text role="muted">{currentLocationLabel}</Text>
                </Stack>
                {here ? (
                    <Badge tone="neutral">Here</Badge>
                ) : (
                    <Badge tone="accent">Pick</Badge>
                )}
            </Stack>
        </button>
    );
}
